import {
  MessagePathNode,
  type MessagePathPart,
  PathParser,
} from "@/shared/domain/topics";
import type { TopicData } from "@/shared/state/visualization/schema/v1";

/**
 * Identifies one message path inside one open file, which is what a position
 * preset stores for each of its axes.
 *
 * The topic name and the message path are separate fields because a topic name
 * can contain dots of its own, so a single dotted string does not say where the
 * topic name ends and the message path begins. The message path stays a list of
 * parts rather than a string so that resolving it needs no parsing and a slice
 * keeps the bounds it selects.
 */
export interface TopicDataPathBinding {
  fileId: string;
  topicName: string;
  messagePath: MessagePathPart[];
}

/**
 * The binding naming the data an axis plots, or undefined when the axis holds a
 * fixed number instead of topic data or is unbound.
 */
export function bindingFor(
  topicData: TopicData | number | undefined,
): TopicDataPathBinding | undefined {
  if (!topicData || typeof topicData === "number") {
    return undefined;
  }

  return {
    fileId: topicData.topic.association.association_id,
    topicName: topicData.topic.name,
    messagePath: topicData.messagePath.parts,
  };
}

/**
 * The topic name and message path of an axis's data joined by a dot, which is
 * the spelling a path selector shows and a user types. Undefined when the axis
 * holds a fixed number instead of topic data or is unbound.
 *
 * For display only. A topic name can contain dots, so the result cannot be
 * split back into a topic name and a message path; use {@link bindingFor}
 * wherever the data has to be resolved again.
 */
export function joinedPathFor(
  topicData: TopicData | number | undefined,
): string | undefined {
  if (!topicData || typeof topicData === "number") {
    return undefined;
  }

  return [
    topicData.topic.name,
    MessagePathNode.partsToString(topicData.messagePath.parts),
  ].join(PathParser.TOPIC_SEPARATOR);
}

/**
 * The topic data an axis plots, or undefined when the axis holds a fixed number
 * instead of topic data or is unbound.
 */
export function topicDataFor(
  value: TopicData | number | undefined,
): TopicData | undefined {
  if (!value || typeof value === "number") {
    return undefined;
  }
  return value;
}
